import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  AdCreativeVersion,
  AdminCreativeReview,
  adminListAdCreatives,
  adminReviewAdCreative,
} from '../../api/ads/ads.api';
import AdPreview from './ad-preview';

const Side: React.FC<{ label: string; c?: AdCreativeVersion | null; format: string }> = ({
  label,
  c,
  format,
}) => (
  <div className="flex-1 min-w-0">
    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">{label}</div>
    {c ? (
      <>
        <AdPreview
          title={c.title || undefined}
          body={c.body || undefined}
          imageUrl={c.imageUrl || undefined}
          videoUrl={c.videoUrl || undefined}
          ctaText={c.ctaText || undefined}
          format={format}
        />
        <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 break-all">Link đích: {c.landingUrl}</div>
      </>
    ) : (
      <p className="text-sm text-gray-500">Không có.</p>
    )}
  </div>
);

/** Tab "Nội dung thay mới": duyệt nội dung quảng cáo mới do chủ chiến dịch gửi. */
const AdminCreativeReviewTab: React.FC = () => {
  const [items, setItems] = useState<AdminCreativeReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const lock = useRef(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setItems(await adminListAdCreatives('Pending'));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Không tải được danh sách nội dung thay mới.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const review = async (it: AdminCreativeReview, approve: boolean) => {
    if (lock.current) return;
    const r = reason.trim();
    if (!approve && (r.length < 5 || r.length > 300)) {
      return void toast.error('Lý do từ chối phải từ 5 đến 300 ký tự.');
    }
    lock.current = true;
    setBusy(it.newCreative.id);
    try {
      await adminReviewAdCreative(it.newCreative.id, approve, approve ? undefined : r);
      toast.success(approve ? 'Đã duyệt, nội dung mới bắt đầu chạy.' : 'Đã từ chối nội dung mới.');
      setRejecting(null);
      setReason('');
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Thao tác thất bại.');
      // Có thể admin khác vừa xử lý nội dung này: tải lại để thấy trạng thái mới nhất.
      setRejecting(null);
      setReason('');
      await load();
    } finally {
      lock.current = false;
      setBusy(null);
    }
  };

  if (loading) return <p className="text-gray-500">Đang tải…</p>;
  if (items.length === 0) return <p className="text-gray-500">Không có nội dung nào đang chờ duyệt.</p>;

  return (
    <div className="space-y-4">
      {items.map((it) => {
        const id = it.newCreative.id;
        const isBusy = busy === id;
        return (
          <div key={id} className="rounded-lg border border-gray-200 dark:border-strokedark dark:bg-boxdark p-3 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold dark:text-white">{it.campaignName}</span>
              <span className="text-xs text-gray-500">{it.format}</span>
              <span className="text-xs text-gray-500">
                Gửi lúc {new Date(it.newCreative.createdAt).toLocaleString('vi-VN')}
              </span>
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <Side label="Đang chạy" c={it.currentCreative} format={it.format} />
              <Side label="Nội dung mới" c={it.newCreative} format={it.format} />
            </div>
            {rejecting === id ? (
              <div className="space-y-2">
                <textarea
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-transparent px-3 py-2 text-sm dark:text-white"
                  rows={2}
                  maxLength={300}
                  placeholder="Lý do từ chối (5 đến 300 ký tự)"
                  value={reason}
                  disabled={isBusy}
                  onChange={(e) => setReason(e.target.value)}
                />
                <div className="flex gap-2">
                  <button
                    disabled={isBusy}
                    onClick={() => review(it, false)}
                    className="rounded-md bg-rose-600 text-white text-sm px-4 py-1.5 disabled:opacity-50"
                  >
                    {isBusy ? 'Đang xử lý…' : 'Xác nhận từ chối'}
                  </button>
                  <button
                    disabled={isBusy}
                    onClick={() => {
                      setRejecting(null);
                      setReason('');
                    }}
                    className="rounded-md border border-gray-300 dark:border-gray-600 text-sm px-4 py-1.5 dark:text-white"
                  >
                    Huỷ
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <button
                  disabled={isBusy || busy !== null}
                  onClick={() => review(it, true)}
                  className="rounded-md bg-emerald-600 text-white text-sm px-4 py-1.5 disabled:opacity-50"
                >
                  {isBusy ? 'Đang xử lý…' : 'Duyệt'}
                </button>
                <button
                  disabled={busy !== null}
                  onClick={() => {
                    setRejecting(id);
                    setReason('');
                  }}
                  className="rounded-md border border-rose-300 text-rose-600 text-sm px-4 py-1.5 disabled:opacity-50"
                >
                  Từ chối
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AdminCreativeReviewTab;
